import { FiGrid, FiLayout } from 'react-icons/fi';
import {
    SectionCard,
    SectionCardH1,
    SectionCardHeading,
    SectionCardSubH1,
} from '../SectionCard';
import { filterHealthStats, filterNames } from 'utils/filters';
import { paidPostCSVColumn, shopPostCSVColumn } from './topPostsCSVColumns';
import { useCallback, useEffect, useState } from 'react';
import { useCurrentCountry, useFilter, useSort } from 'utils/hooks';
import { Button } from '@/ui/design-system/components/ui/button';
import SentimentFilters from './SentimentFilters';
import SortableTopPosts from './SortableTopPosts';
import Stack from '../Stack';
import { TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import TikTokPostsNoDataMessage from '../TikTokPostsNoDataMessage';
import TopPostsDownloadButton from './TopPostsDownloadButton';
import TopPostsTable from './TopPostsTable';
import { cn } from '@/ui/design-system/utils/cn';
import sortDataByKey from 'utils/sortDataByKey';
import styled from 'styled-components';
import { useTopPosts } from 'utils/hooks/api';
import { useTopPostsControls } from './useTopPostsControls.hook';

const TabList = ({ children, className, ...props }) => (
    <div
        className={cn(
            'inline-flex items-center rounded-md border border-input p-0.5 gap-0.5',
            className,
        )}
        {...props}
    >
        {children}
    </div>
);

const TabButton = ({ intent, IconLeft, className, ...props }) => (
    <Button
        variant={intent === 'primary' ? 'secondary' : 'ghost'}
        size="icon"
        className={cn('h-8 w-8 border-0', className)}
        {...props}
    >
        {IconLeft && <IconLeft />}
    </Button>
);
const Capitalize = styled.span`
    text-transform: capitalize;
`;

const DEFAULT_CARD_METRICS = [
    [TIKTOK_METADATA_BY_KEY.last_week_views, true],
    [TIKTOK_METADATA_BY_KEY.total_post_views, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_WoW, false],
    [TIKTOK_METADATA_BY_KEY.create_timestamp, false],
];

const DEFAULT_TABLE_METRICS = [
    [TIKTOK_METADATA_BY_KEY.sentiment_score, true],
    [TIKTOK_METADATA_BY_KEY.last_week_views, true],
    [TIKTOK_METADATA_BY_KEY.total_post_views, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_WoW, true],
    [TIKTOK_METADATA_BY_KEY.create_timestamp, true],
];

export const DISPLAY_MODES = {
    CARDS: 'CARDS',
    TABLE: 'TABLE',
};

function TikTokPostsNoDataAppliedFiltersMessage() {
    return <>No posts match the selected filters.</>;
}

function TopPostsSection({
    year,
    week,
    categories,
    brands,
    countries,
    name,
    displayQty = 5,
    handleFilter = {
        name: filterNames,
        healthStats: filterHealthStats,
    },
    handleSort = sortDataByKey,
    initialFilterOption = {},
    defaultDisplayMode = DISPLAY_MODES.CARDS,
    perPage = 500,
    tableName = 'top-posts-section',
    cardMetrics = DEFAULT_CARD_METRICS,
    tableMetrics = DEFAULT_TABLE_METRICS,
    ...props
}) {
    const [displayMode, setDisplayMode] = useState(defaultDisplayMode);
    const country = useCurrentCountry();
    const {
        sortSelect,
        value,
        defaultSortOption,
        visibleOrderedMetrics,
        columnsMenu,
    } = useTopPostsControls({
        week,
        year,
        mixpanelContext: 'TopPostsSortSelect',
        tableName:
            displayMode === DISPLAY_MODES.TABLE
                ? `${tableName}-table`
                : `${tableName}-cards`,
        metrics:
            displayMode === DISPLAY_MODES.CARDS ? cardMetrics : tableMetrics,
    });
    const unfilteredTopPostsQuery = useTopPosts({
        year: value.year,
        week: value.week,
        countries,
        categories,
        brands,
        rank_type: value.apiRankType,
        per_page: perPage,
        order_by:
            brands?.length > 1 || brands === '*'
                ? defaultSortOption.sortBy
                : 'post_rank',
    });
    const { filteredData, filters, filtersKey, setFilter, setFilters } =
        useFilter(
            unfilteredTopPostsQuery.data || [],
            handleFilter,
            initialFilterOption,
        );
    const { setSortOption, sortOption, sortOptionKey, sortedData } = useSort(
        filteredData,
        handleSort,
        defaultSortOption,
    );
    const nameFilter =
        filters && filters.name && filters.name.length ? filters.name[0] : null;
    useEffect(() => {
        if (displayMode === DISPLAY_MODES.CARDS) {
            const { name, ...rest } = filters;
            setFilters(rest);
            setSortOption(defaultSortOption);
        }
    }, [displayMode]);
    useEffect(() => {
        setSortOption(defaultSortOption);
    }, [value.rankType]);
    const onSearchInputChange = useCallback(
        (value) => {
            try {
                if (value && value !== nameFilter) {
                    setFilter('name', [value]);
                } else if (!value && nameFilter) {
                    setFilter('name', []);
                }
            } catch (err) {
                console.error(err);
            }
        },
        [nameFilter, setFilter],
    );

    const topPostsQuery = { ...unfilteredTopPostsQuery };
    if (displayMode === DISPLAY_MODES.CARDS) {
        topPostsQuery.data = filteredData;
    } else if (displayMode === DISPLAY_MODES.TABLE) {
        topPostsQuery.data = sortedData;
    }
    topPostsQuery.data = topPostsQuery.data?.map((row) => ({
        ...row,
        fetching: false,
        fetchingData: false,
    }));

    return (
        <SectionCard>
            <SectionCardHeading {...props}>
                <SectionCardH1>Top Posts</SectionCardH1>
                {name ? (
                    <SectionCardSubH1>
                        for <Capitalize>{name}</Capitalize>
                    </SectionCardSubH1>
                ) : null}
                <div css="margin-left: auto;">
                    <TopPostsDownloadButton
                        year={value.year}
                        week={value.week}
                        name={name}
                        country={country}
                        source="tiktok"
                        topPostsQuery={topPostsQuery}
                        visibleOrderedMetrics={visibleOrderedMetrics}
                        csvColumns={
                            displayMode === DISPLAY_MODES.CARDS
                                ? null
                                : [
                                      { key: 'url', label: 'Post' },
                                      paidPostCSVColumn,
                                      shopPostCSVColumn,
                                      {
                                          key: 'sentiment_summary',
                                          label: 'Sentiment Summary (AI-generated)',
                                      },
                                      ...visibleOrderedMetrics.map(
                                          ({
                                              key,
                                              tableHeadColumnLabel: label,
                                          }) =>
                                              key === 'last_week_views'
                                                  ? { key, label: 'Views' }
                                                  : { key, label },
                                      ),
                                  ]
                        }
                    />
                </div>
            </SectionCardHeading>
            <Stack
                $space=".5rem"
                $direction="row"
                css={`
                    display: flex;
                    align-items: flex-start;
                `}
            >
                {sortSelect}
                <SentimentFilters
                    posts={unfilteredTopPostsQuery.data}
                    filters={filters}
                    setFilter={setFilter}
                />
                <TabList>
                    <TabButton
                        IconLeft={FiGrid}
                        intent={
                            displayMode === DISPLAY_MODES.CARDS
                                ? 'primary'
                                : 'secondary'
                        }
                        onClick={() => setDisplayMode(DISPLAY_MODES.CARDS)}
                        mixpanelContext="TopPostsViewCards"
                    />
                    <TabButton
                        IconLeft={FiLayout}
                        intent={
                            displayMode === DISPLAY_MODES.TABLE
                                ? 'primary'
                                : 'secondary'
                        }
                        onClick={() => setDisplayMode(DISPLAY_MODES.TABLE)}
                        mixpanelContext="TopPostsViewTable"
                    />
                </TabList>
                {columnsMenu}
            </Stack>
            {displayMode === DISPLAY_MODES.CARDS ? (
                <SortableTopPosts
                    topPostsQuery={topPostsQuery}
                    displayQty={displayQty}
                    sortOption={defaultSortOption}
                    visibleOrderedMetrics={visibleOrderedMetrics}
                    NoDataMessage={
                        unfilteredTopPostsQuery.data?.length &&
                        !sortedData?.length
                            ? TikTokPostsNoDataAppliedFiltersMessage
                            : TikTokPostsNoDataMessage
                    }
                />
            ) : (
                <TopPostsTable
                    year={value.year}
                    week={value.week}
                    topPostsQuery={topPostsQuery}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    visibleOrderedMetrics={visibleOrderedMetrics}
                    name={name}
                    onSearchInputChange={onSearchInputChange}
                    dataKey={`${filtersKey}-${sortOptionKey}`}
                    noDataMessage={
                        unfilteredTopPostsQuery.data?.length &&
                        !sortedData?.length ? (
                            <TikTokPostsNoDataAppliedFiltersMessage />
                        ) : (
                            <TikTokPostsNoDataMessage />
                        )
                    }
                    isLoading={topPostsQuery.isLoading || topPostsQuery.isIdle}
                />
            )}
        </SectionCard>
    );
}

export default TopPostsSection;
